import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';

// --- Email & SMS Sending Helper ---
export const sendEmail = async (to, subject, html, phone = null) => {
    try {
        const sendBrevoNotification = httpsCallable(functions, 'sendBrevoNotification');
        const textContent = html.replace(/<[^>]*>?/gm, '\n').replace(/\n+/g, '\n').trim();
        
        let cleanPhone = null;
        if (phone) {
            cleanPhone = phone.replace(/[^\d+]/g, '');
            if (!cleanPhone.startsWith('+')) {
                cleanPhone = '+1' + cleanPhone;
            }
        }
        
        await sendBrevoNotification({ 
            toEmail: to || null, 
            toPhone: cleanPhone,
            subject: subject, 
            textContent: textContent,
            htmlContent: html 
        });
        console.log(`Notification sent successfully to ${to || cleanPhone}`);
    } catch (error) {
        console.error("Failed to send notification via Brevo:", error);
        throw error;
    }
};

// --- Notification Helper ---
export const createNotification = (userId, message, destination = null) => {
    if (!userId) return Promise.resolve();
    return addDoc(collection(db, 'notifications'), {
        userId,
        message,
        isRead: false,
        createdAt: serverTimestamp(),
        destination
    });
};

// --- Data Sanitization Helper ---
export const sanitizeData = (data) => {
    const clean = {};
    Object.keys(data).forEach(key => {
        if (data[key] !== undefined) {
            clean[key] = data[key];
        }
    });
    return clean;
};

// --- Geocoding & Distance Helpers ---
export const geocodeAddress = async (address) => {
    if (!window.google || !window.google.maps || !window.google.maps.Geocoder) return null;
    const geocoder = new window.google.maps.Geocoder();
    return new Promise((resolve) => {
        geocoder.geocode({ address }, (results, status) => {
            if (status === 'OK' && results[0]) {
                resolve({
                    lat: results[0].geometry.location.lat(),
                    lng: results[0].geometry.location.lng()
                });
            } else {
                resolve(null);
            }
        });
    });
};

export const getDistance = (p1, p2) => {
    if (!p1 || !p2) return Infinity;
    const R = 3958.8; // Radius of the Earth in miles
    const dLat = (p2.lat - p1.lat) * Math.PI / 180;
    const dLon = (p2.lng - p1.lng) * Math.PI / 180;
    const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(p1.lat * Math.PI / 180) * Math.cos(p2.lat * Math.PI / 180) * Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    return R * c; 
};

// --- Tax Rate Helper ---
export const getTaxRate = (zip) => {
    if (!zip) return 0.0;
    const cleanZip = zip.toString().replace(/[^0-9]/g, '').slice(0, 5);
    if (cleanZip.length < 3) return 0.065; // Fallback if invalid
    
    const prefix = parseInt(cleanZip.substring(0, 3));
    
    if (prefix >= 900 && prefix <= 961) return 0.0882; 
    if (prefix >= 100 && prefix <= 149) return 0.0852; 
    if (prefix >= 750 && prefix <= 799) return 0.0820; 
    if (prefix >= 320 && prefix <= 349) return 0.0702;
    if (prefix >= 600 && prefix <= 629) return 0.0881;
    if (prefix >= 150 && prefix <= 196) return 0.0634;
    if (prefix >= 430 && prefix <= 458) return 0.0722;
    if (prefix >= 300 && prefix <= 319) return 0.0735;
    if (prefix >= 480 && prefix <= 499) return 0.0600;
    if (prefix >= 70 && prefix <= 89) return 0.0660;
    if (prefix >= 980 && prefix <= 994) return 0.0929;
    if (prefix >= 850 && prefix <= 865) return 0.0837;
    if (prefix >= 10 && prefix <= 27) return 0.0625;
    if (prefix >= 370 && prefix <= 385) return 0.0955;
    if (prefix >= 889 && prefix <= 898) return 0.0823;
    if (prefix >= 800 && prefix <= 816) return 0.0772;

    return 0.065; // National Average fallback
};

// --- Image Compression Helper ---
export const compressImage = (file, maxWidth, quality) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                if (width > maxWidth) {
                    height = Math.round((height * maxWidth) / width);
                    width = maxWidth;
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob((blob) => {
                    resolve(new File([blob], file.name, { type: 'image/jpeg' }));
                }, 'image/jpeg', quality);
            };
            img.onerror = (err) => reject(err);
        };
        reader.onerror = (err) => reject(err);
    });
};